import { useEffect, useMemo, useState } from 'react';
import { leer as leerLocal, escribir as escribirLocal } from '../lib/almacenLocal';
import { totalDelDia } from '../lib/estadoDelDia';
import { tipoPorDefecto } from '../lib/tipoDelDia';
import { excesos, margenDe, MARGEN } from '../lib/exceso';

/**
 * El día de hoy para el Inicio rediseñado: las tomas en su orden, lo que lleva cada una y
 * lo que llevas en el día. Es la misma cuenta que `components/inicio/TuDietaHoy` (mismas
 * llamadas, mismo reparto, misma marca por comida); solo cambia quién la pinta.
 */

const ORDEN_PERI = ['Intra', 'Post'];

const montadoDe = (comida) => (comida?.alimentos || []).reduce((acc, a) => {
    const m = a.macros_efectivos || {};
    return { P: acc.P + (m.P || 0), H: acc.H + (m.H || 0), G: acc.G + (m.G || 0) };
}, { P: 0, H: 0, G: 0 });

const servidoDe = (dieta, k) => {
    const s = dieta?.servido_por_comida?.[k];
    if (s) return { P: s.P || 0, H: s.H || 0, G: s.G || 0 };
    return montadoDe(dieta?.comidas?.[k]);
};

/** 'vacia' | 'cuadrada' | 'sobra' | 'falta', con la misma vara que Nutrición (`getMealStatus`). */
export const estadoDeLaToma = (montado, objetivo, esPeri, tieneAlimentos) => {
    if (!tieneAlimentos) return 'vacia';
    if (!objetivo) return null;
    const ok = (m) => Math.abs((objetivo[m] || 0) - (montado[m] || 0)) <= margenDe(objetivo[m]);
    if (ok('P') && ok('H') && (esPeri || ok('G'))) return 'cuadrada';
    if (excesos(montado, objetivo, { margen: MARGEN, esPeri }).length) return 'sobra';
    return 'falta';
};

export const nombreDeLaToma = (k, unica, ultima) => {
    if (ORDEN_PERI.includes(k)) return k;
    if (unica) return 'Comida única';
    if (k === 'C1') return 'Desayuno';
    if (k === ultima) return 'Cena';
    return `Comida ${k.slice(1)}`;
};

export function useDiaDeHoy({ api, userId, fecha, dieta, objetivo, activo }) {
    const [reparto, setReparto] = useState(null);
    const [repartoResuelto, setRepartoResuelto] = useState(false);
    const [marcadas, setMarcadas] = useState({});
    const [extras, setExtras] = useState([]);

    const comidasGuardadas = useMemo(() => (dieta?.exists && dieta.comidas) || {}, [dieta]);
    const diaConfigurado = Boolean(dieta?.exists && dieta.num_comidas);

    useEffect(() => { setExtras((dieta?.exists && dieta.extras) || []); }, [dieta]);

    useEffect(() => {
        let delNavegador = {};
        try { delNavegador = JSON.parse(leerLocal(`inicio-marcadas-${fecha}`, userId) || '{}') || {}; }
        catch { delNavegador = {}; }
        const delServidor = Object.fromEntries(
            Object.entries(comidasGuardadas).filter(([, c]) => c?.marcada === true).map(([k]) => [k, true]));
        setMarcadas({ ...delNavegador, ...delServidor });
    }, [fecha, userId, comidasGuardadas]);

    useEffect(() => {
        if (!activo) return undefined;
        let cancelado = false;
        setRepartoResuelto(false);
        const cargar = async () => {
            try {
                let cfg;
                if (diaConfigurado) {
                    cfg = {
                        tipo_dia: dieta.tipo_dia || 'entrenamiento',
                        num_comidas: dieta.num_comidas || 4,
                        momento_entreno: dieta.momento_entreno != null ? dieta.momento_entreno : 1,
                        opcion_peri: dieta.opcion_peri || 'intra_post',
                    };
                } else {
                    const r = await api.get('/user/diet-config').catch(() => ({ data: {} }));
                    cfg = {
                        tipo_dia: dieta?.tipo_dia_previsto || tipoPorDefecto(fecha),
                        num_comidas: r.data?.num_comidas || 4,
                        momento_entreno: r.data?.momento_entreno != null ? r.data.momento_entreno : 1,
                        opcion_peri: r.data?.opcion_peri || 'intra_post',
                    };
                }
                const res = await api.post('/calculator/distribute', {
                    fecha, ...cfg, single_meal: cfg.num_comidas === 1,
                });
                if (!cancelado) { setReparto(res.data); setRepartoResuelto(true); }
            } catch (err) {
                if (!cancelado) setRepartoResuelto(true);
                console.error('[inicio] no se pudo traer el reparto del día', err);
            }
        };
        cargar();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, fecha, dieta, activo]);

    const marcar = (k) => {
        setMarcadas((prev) => {
            const sig = { ...prev, [k]: !prev[k] };
            escribirLocal(`inicio-marcadas-${fecha}`, userId, JSON.stringify(sig));
            api.patch(`/diets/${fecha}/comida-marcada`, { comida: k, marcada: !prev[k] })
                .catch((e) => console.error('No se pudo guardar la marca de la comida', e));
            return sig;
        });
    };

    const objetivoConPeri = reparto?.resumen
        ? { P: reparto.resumen.P_total || 0, H: reparto.resumen.H_total || 0, G: reparto.resumen.G_total || 0 }
        : objetivo;

    const numComidas = diaConfigurado ? dieta.num_comidas
        : (reparto?.comidas ? Object.keys(reparto.comidas).length : 4);
    const esUnica = numComidas === 1;
    const claves = ['C1', 'C2', 'C3', 'C4'].slice(0, Math.max(1, Math.min(4, numComidas)));

    const periPorBloque = useMemo(() => {
        const objetivos = reparto?.periworkout || {};
        const res = {};
        for (const k of ORDEN_PERI) {
            const tiene = (comidasGuardadas[k]?.alimentos || []).length > 0;
            const obj = objetivos[k];
            if (!tiene && !obj) continue;
            const montado = servidoDe(dieta, k);
            res[k] = {
                tiene,
                montado,
                objetivo: obj ? { P: obj.P || 0, H: obj.H || 0, G: 0 } : null,
                suma: tiene ? { ...montado, G: 0 } : { P: obj.P || 0, H: obj.H || 0, G: 0 },
            };
        }
        return res;
    }, [dieta, reparto, comidasGuardadas]);

    const momentoEntreno = diaConfigurado
        ? (dieta.momento_entreno != null ? dieta.momento_entreno : 1)
        : (reparto?.config?.momento_entreno != null ? reparto.config.momento_entreno : 1);
    const trasComida = Math.max(0, Math.min(claves.length, Number(momentoEntreno) || 0));

    const tomas = useMemo(() => {
        const clavesPeri = ORDEN_PERI.filter((k) => periPorBloque[k]);
        const orden = [];
        const meterPeri = () => clavesPeri.forEach((k) => orden.push({ clave: k, esPeri: true }));
        if (trasComida === 0) meterPeri();
        claves.forEach((k, i) => {
            orden.push({ clave: k, esPeri: false });
            if (i + 1 === trasComida) meterPeri();
        });
        return orden.map(({ clave, esPeri }) => {
            const peri = esPeri ? periPorBloque[clave] : null;
            const alimentos = comidasGuardadas[clave]?.alimentos || [];
            const montado = esPeri ? peri.montado : servidoDe(dieta, clave);
            return {
                clave,
                esPeri,
                nombre: nombreDeLaToma(clave, esUnica, claves[claves.length - 1]),
                alimentos: alimentos.map((a) => a.nombre).filter(Boolean),
                // Solo para pintar: el nombre con su cantidad («Arroz 80 g»). No se envía a ningún sitio.
                alimentosConCantidad: alimentos.filter((a) => a.nombre).map((a) => {
                    const g = Number(a.cantidad_g);
                    return g > 0 ? `${a.nombre} ${Math.round(g)} ${a.es_liquido ? 'ml' : 'g'}` : a.nombre;
                }),
                tieneAlimentos: alimentos.length > 0,
                montado: esPeri ? { ...montado, G: 0 } : montado,
                objetivo: esPeri ? peri.objetivo : (reparto?.comidas?.[clave] || null),
                suma: esPeri ? peri.suma : montado,
                marcada: !!marcadas[clave],
                estado: estadoDeLaToma(esPeri ? { ...montado, G: 0 } : montado,
                    esPeri ? peri.objetivo : (reparto?.comidas?.[clave] || null), esPeri, alimentos.length > 0),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claves.join(','), periPorBloque, trasComida, comidasGuardadas, dieta, reparto, marcadas, esUnica]);

    const llevas = useMemo(() => {
        const deComidas = tomas.filter((t) => t.marcada).reduce((acc, t) => ({
            P: acc.P + (t.suma.P || 0), H: acc.H + (t.suma.H || 0), G: acc.G + (t.suma.G || 0),
        }), { P: 0, H: 0, G: 0 });
        return (extras || []).reduce((acc, e) => {
            const m = e?.macros;
            if (!m) return acc;
            return { P: acc.P + (m.P || 0), H: acc.H + (m.H || 0), G: acc.G + (m.G || 0) };
        }, deComidas);
    }, [tomas, extras]);

    const enLaDieta = dieta?.servido_por_comida ? totalDelDia(dieta.servido_por_comida) : null;

    return {
        tomas, llevas, objetivo: objetivoConPeri, enLaDieta, repartoResuelto,
        marcar, extras, setExtras,
    };
}
